import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";

export default function Guides() {
  const [expandedSection, setExpandedSection] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    document.title = "TOC > Write Off Guides";
  }, []);

  const toggleSection = (section) => {
    // If clicking the same section, close it. Otherwise, open the new one
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  // Scroll to opened section with smooth animation
  useEffect(() => {
    if (expandedSection && sectionRefs.current[expandedSection]) {
      setTimeout(() => {
        sectionRefs.current[expandedSection]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100); // Small delay to allow animation to start
    }
  }, [expandedSection]);

  const guides = [
    {
      id: "dancer",
      title: "Dancer Write Off Guide",
      content: "As a dancer, you can write off various business expenses related to your profession. This includes costumes, dancewear, shoes, makeup, hair styling, and other appearance-related expenses. You may also deduct travel expenses when traveling for performances, professional development courses, and equipment used for your work. Keep detailed records of all expenses and maintain receipts for tax purposes. Additionally, you can deduct a portion of your home if you use it as a studio or practice space, as well as any professional memberships or licenses required for your work.",
    },
    {
      id: "kink",
      title: "Kink Write Off Guide",
      content: "For individuals in the kink industry, business expenses can include professional attire, equipment, safety gear, and educational materials related to your work. You may deduct expenses for professional development workshops, certifications, and training courses. Travel expenses for professional events, conventions, or client meetings are also deductible. Additionally, you can write off a portion of your home office if you use it for business purposes, professional photography or videography services, website maintenance, and marketing expenses. Keep meticulous records of all business-related expenses.",
    },
    {
      id: "creator",
      title: "Creator Write Off Guide",
      content: "Content creators can deduct a wide range of business expenses including equipment such as cameras, lighting, computers, and software. You may also write off expenses for props, costumes, sets, and production materials. Professional services like editing, graphic design, and marketing are deductible. Home office expenses, internet and phone bills (business portion), and subscription services used for content creation can be written off. Travel expenses for shoots, events, or collaborations are also deductible. Don't forget to track expenses for professional development courses, equipment maintenance, and any platform fees or commissions.",
    },
    {
      id: "sugar-baby",
      title: "Sugar Baby Write Off Guide",
      content: "As a sugar baby, you can deduct business-related expenses such as professional attire, grooming, beauty treatments, and appearance-related costs that are necessary for your work. Travel expenses for meeting clients or attending events are deductible. You may also write off expenses for professional photography, website maintenance, and marketing materials. Educational expenses related to improving your skills or business knowledge can be deducted. Keep detailed records of all expenses, including receipts and documentation. Additionally, you can deduct a portion of your phone and internet bills if used for business purposes, as well as any professional memberships or subscriptions related to your work.",
    },
  ];

  return (
    <div className="w-full max-w-full sm:max-w-[500px] min-h-screen md:min-h-[550px] flex-col bg-pink-50 flex items-center mx-auto">
      <Header />

      <div className="w-full px-2 sm:px-4 py-3 sm:py-6 flex-1">
        <div className="bg-[#f3ebe3] rounded-md shadow-xl border border-gray-300 overflow-hidden">
          {/* Back Button */}
          <div className="w-full flex items-center justify-between px-3 pt-3">
            <div className="w-10 flex justify-center">
              <Link
                to="/tax-help"
                className="w-10 h-10 text-[#f7adc4] rounded-full font-semibold transition transform hover:-translate-x-0.5 hover:scale-[1.02] active:scale-[0.98] grid place-items-center"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4A1 1 0 018.707 6.293L6.414 8.586H17a1 1 0 110 2H6.414l2.293 2.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
            </div>
            <h2 className="flex-1 font-semibold text-center text-sm sm:text-base md:text-[17px] tracking-wide">
              Write Off Guides
            </h2>
            <div className="w-10" />
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-3">
            {guides.map((guide) => (
              <div
                key={guide.id}
                ref={(el) => (sectionRefs.current[guide.id] = el)}
                className="bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(guide.id)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#f7adc4] text-white hover:bg-[#f49fb8] transition-colors"
                >
                  <span className="font-semibold text-sm sm:text-base flex items-center gap-2">
                    {guide.title}
                    <svg
                      className={`h-4 w-4 transition-transform duration-300 ${
                        expandedSection === guide.id ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    expandedSection === guide.id
                      ? "max-h-[1000px] opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-4 py-3 text-sm sm:text-base text-gray-700 leading-relaxed">
                    {guide.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full bg-[#f7adc4] text-white text-center flex justify-center items-center px-4 py-2 mt-auto">
        <img src="/1.png" alt="Logo" className="h-auto w-16 sm:w-20" />
      </div>
    </div>
  );
}
